import { useState, useEffect, useRef } from 'react';
import { Modal, Table, Avatar, App, Spin, Typography } from 'antd';
import dayjs from 'dayjs';
import { getHallExpertSorts } from '@services/Conference';
import { expertLevels } from '@utils/constants';
import { EMPTY_PLACEHOLDER } from '../../constants';

const { Text } = Typography;

/**
 * 专家等级映射，与专家列表/编辑弹窗保持一致
 */
const EXPERT_LEVEL_MAP = expertLevels.reduce((acc, item) => {
    acc[item.level] = item.label;
    return acc;
}, {});

/**
 * 格式化论坛日期为 xxxx年x月x日
 * @param {string|null|undefined} dateStr - 接口 dates 字段
 * @returns {string}
 */
function formatForumDate(dateStr) {
    if (!dateStr) return '';
    const d = dayjs(dateStr);
    if (!d.isValid()) return dateStr;
    return d.format('YYYY年M月D日');
}

/**
 * 关联专家子弹窗
 * @param {Object} props
 * @param {boolean} props.open - 是否打开
 * @param {number} props.hallId - 会议 ID
 * @param {string} props.hallName - 论坛名称
 * @param {string} props.dates - 论坛日期（接口 benefitList.dates）
 * @param {number[]} props.selectedExpertIds - 已选中的专家 ID 数组
 * @param {Function} props.onConfirm - 确认回调 (expertIds, experts) => void
 * @param {Function} props.onCancel - 取消回调
 */
export default function AssociateExpertModal({ open, hallId, hallName, dates, selectedExpertIds = [], onConfirm, onCancel }) {
    const { modal } = App.useApp();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const initialSelectedKeysRef = useRef([]);
    /**
     * 用 ref 转存父组件传入的 selectedExpertIds，避免 useEffect 因引用变化重复请求并重置用户选择
     * 父组件 `expertIds || []` 会在每次重渲染时生成新数组字面量，依赖该 prop 会触发非预期重跑
     */
    const selectedExpertIdsRef = useRef(selectedExpertIds);
    selectedExpertIdsRef.current = selectedExpertIds;

    /**
     * 加载专家列表数据：仅在弹窗打开或会议变更时触发
     */
    useEffect(() => {
        if (!open || !hallId) {
            return;
        }

        const fetchData = async () => {
            setLoading(true);
            try {
                const res = await getHallExpertSorts({ hallId });
                if (res.status_code === 200 && res.data?.sortedExperts) {
                    const experts = res.data.sortedExperts || [];
                    setData(experts);
                    // 从 ref 取最新值，初始化时即为打开瞬间的选中态
                    const currentSelected = selectedExpertIdsRef.current || [];
                    const initialKeys = currentSelected.length > 0 ? currentSelected.filter(id => id != null) : experts.filter(e => e.selected === 1).map(e => e.id);
                    setSelectedRowKeys(initialKeys);
                    initialSelectedKeysRef.current = initialKeys;
                }
            } catch (error) {
                console.error('Failed to load experts:', error);
                setData([]);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [open, hallId]);

    /**
     * 重置状态
     */
    const resetState = () => {
        setData([]);
        setSelectedRowKeys([]);
        initialSelectedKeysRef.current = [];
    };

    /**
     * 确认按钮：回传选中的专家 ID 和专家对象
     */
    const handleConfirm = () => {
        const selectedExperts = data.filter(e => selectedRowKeys.includes(e.id));
        onConfirm(selectedRowKeys, selectedExperts);
        resetState();
    };

    /**
     * 取消按钮 - 脏检查
     */
    const handleCancel = () => {
        const initialKeys = JSON.stringify([...initialSelectedKeysRef.current].sort((a, b) => a - b));
        const currentKeys = JSON.stringify([...selectedRowKeys].sort((a, b) => a - b));

        if (initialKeys !== currentKeys) {
            modal.confirm({
                title: '提示',
                content: '发现有数据变动，确认不保存并关闭？',
                okText: '确认',
                cancelText: '取消',
                centered: true,
                onOk: () => {
                    resetState();
                    onCancel();
                },
            });
        } else {
            resetState();
            onCancel();
        }
    };

    /**
     * Table 列定义
     */
    const columns = [
        {
            title: '基本信息',
            dataIndex: 'memUsername',
            render: (_, record) => {
                const levelText = EXPERT_LEVEL_MAP[record.expertLevel] ?? EMPTY_PLACEHOLDER;
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Avatar src={record.expertAvatar || undefined} size={40}>
                            {record.memUsername?.[0]}
                        </Avatar>
                        <div style={{ lineHeight: 1.4 }}>
                            <div>
                                {record.memUsername}
                                <Text type="secondary" style={{ marginLeft: 4 }}>{`(专家ID: ${record.id})`}</Text>
                            </div>
                            <div>
                                <Text type="secondary">{levelText}</Text>
                            </div>
                            <div>
                                <Text type="secondary">{record.memMobile || EMPTY_PLACEHOLDER}</Text>
                            </div>
                        </div>
                    </div>
                );
            },
        },
        {
            title: '单位任职',
            dataIndex: 'ssoMemberWorkExperiencesEntities',
            render: experiences => {
                if (!experiences || experiences.length === 0) return EMPTY_PLACEHOLDER;
                const current = experiences.find(e => e.current === 1) || experiences[0];
                const parts = [current.unitName, current.unitOffice, current.unitPost].filter(Boolean);
                return parts.length > 0 ? parts.join(' - ') : EMPTY_PLACEHOLDER;
            },
        },
    ];

    /**
     * Table rowSelection 配置
     */
    const rowSelection = {
        selectedRowKeys,
        onChange: keys => setSelectedRowKeys(keys),
    };

    const forumDateText = formatForumDate(dates);

    return (
        <Modal title="关联专家" open={open} onOk={handleConfirm} onCancel={handleCancel} centered width={700} okText="确认" cancelText="取消">
            <Spin spinning={loading}>
                {/* 论坛信息 */}
                {(forumDateText || hallName) && (
                    <div style={{ marginBottom: 12 }}>
                        {forumDateText && <div>日期：{forumDateText}</div>}
                        {hallName && <div>论坛：{hallName}</div>}
                    </div>
                )}
                <Table rowKey="id" columns={columns} dataSource={data} rowSelection={rowSelection} size="small" pagination={false} scroll={{ y: 400 }} />
            </Spin>
        </Modal>
    );
}
